import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { tableCellClasses } from "@mui/material/TableCell";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
    border: "solid 1px #fff",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "0px 8px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  height: "40px",
}));

export default function TableMultiple({
  filterValue,
  formBody,
  setFormBody,
  currentPage,
  setCurrentPage,
  needRefresh,
  setNeedRefresh,
  selectedRetailer,
  setSelectionQuery,
}) {
  const accessToken = Cookies.get("access_token") || "";

  const [isLoading, setIsLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const itemsPerPageList = [5, 10, 50, 100];
  const [totalItems, setTotalItems] = useState("");

  const [dataTable, setDataTable] = useState([]);

  async function fetchMainData() {
    setDataTable([]);
    setIsLoading(true);

    const queryParams = new URLSearchParams();

    queryParams.append("page", currentPage);
    queryParams.append("size", itemsPerPage);
    queryParams.append("search_term", filterValue);
    queryParams.append("retailer", selectedRetailer);
    queryParams.append("category_mode", "reset");

    const queryString = queryParams.toString();
    const apiUrl =
      `${process.env.REACT_APP_API_URL}/api/v1/data-processing-raw-crosscoding/rta_mt/product` +
      (queryString ? `?${queryString}` : "");

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTotalItems(data.pagination_info.total);
        setDataTable(data.data);
      } else {
        setDataTable([]);
      }
    } catch (error) {
      setDataTable([]);
    }

    setIsLoading(false);
  }

  const handleChange = (row) => () => {
    setFormBody((prev) => {
      const exists = prev.multiple_selection?.some(
        (item) =>
          item.ret_category === row.ret_category &&
          item.ret_brand === row.ret_brand &&
          item.ret_product === row.ret_product &&
          item.idbrand === row.idbrand &&
          item.idcategory === row.idcategory
      );

      const updated = exists
        ? prev.multiple_selection.filter(
            (item) =>
              !(
                item.ret_category === row.ret_category &&
                item.ret_brand === row.ret_brand &&
                item.ret_product === row.ret_product &&
                item.idbrand === row.idbrand &&
                item.idcategory === row.idcategory
              )
          )
        : [
            ...(prev.multiple_selection || []),
            {
              ret_category: row.ret_category,
              ret_brand: row.ret_brand,
              ret_product: row.ret_product,
              idbrand: row.idbrand,
              idcategory: row.idcategory,
            },
          ];

      return {
        ...prev,
        multiple_selection: updated,
      };
    });
  };

  // pagination
  const handlePageChange = (event, page) => {
    setCurrentPage(page + 1);
  };

  const handleChangeItemPerPageSelect = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (selectedRetailer) {
        fetchMainData();
      }
    }, 700);
    return () => {
      clearTimeout(handler);
    };
  }, [accessToken, selectedRetailer, filterValue, currentPage, itemsPerPage]);

  useEffect(() => {
    if (needRefresh) {
      fetchMainData();
      setNeedRefresh(false);
    }
  }, [needRefresh]);

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="checkbox table">
          <TableHead>
            <TableRow sx={{ height: "10px" }}>
              <StyledTableCell>RET_Category</StyledTableCell>
              <StyledTableCell align="left">RET_Brand</StyledTableCell>
              <StyledTableCell align="left">RET_Product</StyledTableCell>
              <StyledTableCell align="left">BRAND</StyledTableCell>
              <StyledTableCell align="left">CATEGORY</StyledTableCell>
              <StyledTableCell align="left">Select</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataTable.length > 0 ? (
              dataTable.map((row) => (
                <StyledTableRow key={`${row.id}`}>
                  <StyledTableCell component="th" scope="row">
                    {row.ret_category}
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    {row.ret_brand}
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    {row.ret_product}
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    {row.brand_name}
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    {row.category_name}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Checkbox
                      checked={formBody.multiple_selection?.some(
                        (item) =>
                          item.ret_category === row.ret_category &&
                          item.ret_brand === row.ret_brand &&
                          item.ret_product === row.ret_product &&
                          item.idbrand === row.idbrand &&
                          item.idcategory === row.idcategory
                      )}
                      sx={{ padding: "0px" }}
                      onChange={handleChange(row)}
                      color="primary"
                    />
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{ border: 1, borderColor: "grey.300" }}
                >
                  {isLoading ? "Loading..." : "No Data Available"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {dataTable.length > 0 && (
        <TablePagination
          rowsPerPageOptions={itemsPerPageList}
          component="div"
          count={totalItems}
          rowsPerPage={itemsPerPage}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleChangeItemPerPageSelect}
        />
      )}
    </>
  );
}
